import { useEffect, useState, type FormEvent } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { to: "/", label: "首页", end: true },
  { to: "/category/movie", label: "电影" },
  { to: "/category/tv", label: "电视剧" },
  { to: "/category/variety", label: "综艺" },
  { to: "/about", label: "关于" },
];

export default function Navbar() {
  const [keyword, setKeyword] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const query = keyword.trim();
    navigate(query ? `/search?q=${encodeURIComponent(query)}` : "/search");
  };

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `px-3 py-2 text-sm rounded-md transition-colors whitespace-nowrap ${
      isActive
        ? "text-primary-600 font-semibold bg-primary-50"
        : "text-foreground-700 hover:text-primary-600 hover:bg-primary-50/60"
    }`;

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="w-full px-4 md:px-8">
        <div className="flex h-16 items-center gap-4 md:gap-8">
          <Link to="/" className="flex items-center gap-2 shrink-0 cursor-pointer">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-play-circle-fill text-xl" aria-hidden="true"></i>
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-lg font-extrabold tracking-tight text-foreground-950">
                91<span className="text-primary-500">视频</span>网站
              </span>
              <span className="mt-0.5 text-[10px] text-foreground-400">热门影视在线观看</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end} className={linkClass}>
                {item.label}
              </NavLink>
            ))}
          </nav>

          <form
            onSubmit={handleSearch}
            className="ml-auto hidden md:flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3 py-1.5 focus-within:border-primary-400 transition-colors"
          >
            <i className="ri-search-line text-base text-foreground-400" aria-hidden="true"></i>
            <input
              type="text"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索电影、电视剧、综艺"
              aria-label="搜索影视资源"
              className="w-40 lg:w-56 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
            />
            <button
              type="submit"
              className="rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50 cursor-pointer hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              搜索
            </button>
          </form>

          <button
            type="button"
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label="打开菜单"
            aria-expanded={menuOpen}
            className="ml-auto md:hidden flex h-10 w-10 items-center justify-center rounded-md text-foreground-800 hover:bg-background-100 cursor-pointer"
          >
            <i className={menuOpen ? "ri-close-line text-2xl" : "ri-menu-line text-2xl"} aria-hidden="true"></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 pb-5 pt-3">
          <form
            onSubmit={handleSearch}
            className="mb-3 flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3 py-2"
          >
            <i className="ri-search-line text-base text-foreground-400" aria-hidden="true"></i>
            <input
              type="text"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索影视资源"
              aria-label="搜索影视资源"
              className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
            />
            <button
              type="submit"
              className="rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50 cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </form>
          <nav className="flex flex-col">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `rounded-md px-3 py-3 text-sm ${
                    isActive
                      ? "bg-primary-50 font-semibold text-primary-600"
                      : "text-foreground-700"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}